import { useState, useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useUser } from '@clerk/react'
import { useQuery } from '@tanstack/react-query'
import Navbar from './components/layout/Navbar'
import Sidebar from './components/layout/Sidebar'
import { ToastProvider } from './components/ui/Toast'
import { useAuthedApi } from './hooks/useAuthedApi'

// Lazy load page components for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'))
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'))
const ProjectPage = lazy(() => import('./pages/projects/ProjectPage'))
const BidRequestPage = lazy(() => import('./pages/projects/BidRequestPage'))
const CanvasPage = lazy(() => import('./pages/canvas/CanvasPage'))
const EstimatePage = lazy(() => import('./pages/estimate/EstimatePage'))
const PricingPage = lazy(() => import('./pages/settings/PricingPage'))
const GeneralContractorsListPage = lazy(() => import('./pages/contractors/GeneralContractorsListPage'))
const IntegrationsPage = lazy(() => import('./pages/integrations/IntegrationsPage'))
const UsersPage = lazy(() => import('./pages/users/UsersPage'))
const ApprovalsPage = lazy(() => import('./pages/approvals/ApprovalsPage'))

// ─── Loading Spinner ──────────────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 'calc(100vh - var(--nav-height))',
        color: 'var(--muted)',
        fontSize: 14,
        gap: 10,
      }}
    >
      <span
        style={{
          width: 18,
          height: 18,
          border: '2px solid var(--border)',
          borderTopColor: 'var(--green)',
          borderRadius: '50%',
          display: 'inline-block',
          animation: 'spin 0.8s linear infinite',
        }}
      />
      Loading…
    </div>
  )
}

// ─── Auth guard ───────────────────────────────────────────────────────────────

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useUser()

  if (!isLoaded) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - var(--nav-height))',
          color: 'var(--muted)',
          fontSize: 14,
          gap: 10,
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            border: '2px solid var(--border)',
            borderTopColor: 'var(--green)',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        Loading…
      </div>
    )
  }

  if (!isSignedIn) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

// ─── Admin auth guard ─────────────────────────────────────────────────────────

interface UserProfile {
  id: string
  role: 'ADMIN' | 'ESTIMATOR'
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useUser()
  const { get } = useAuthedApi()
  const { data: userProfile, isLoading: profileLoading } = useQuery<UserProfile>({
    queryKey: ['userProfile'],
    queryFn: () => get<UserProfile>('/auth/profile'),
    enabled: isLoaded && !!isSignedIn,
  })

  if (!isLoaded || (isLoaded && isSignedIn && profileLoading)) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - var(--nav-height))',
          color: 'var(--muted)',
          fontSize: 14,
          gap: 10,
        }}
      >
        <span
          style={{
            width: 18,
            height: 18,
            border: '2px solid var(--border)',
            borderTopColor: 'var(--green)',
            borderRadius: '50%',
            display: 'inline-block',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        Loading…
      </div>
    )
  }

  if (!isSignedIn) {
    return <Navigate to="/" replace />
  }

  if (userProfile?.role !== 'ADMIN') {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 'calc(100vh - var(--nav-height))',
          color: 'var(--error)',
          fontSize: 14,
        }}
      >
        Access denied. Admin role required.
      </div>
    )
  }

  return <>{children}</>
}

// ─── Home route: redirect if signed in, else show landing ─────────────────────

function HomeRoute() {
  const { isLoaded, isSignedIn } = useUser()

  // Wait for Clerk — render nothing to avoid flash
  if (!isLoaded) return null
  if (isSignedIn) return <Navigate to="/dashboard" replace />
  return <LandingPage />
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  const { isLoaded, isSignedIn } = useUser()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)

  // Track window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Close sidebar when navigating on mobile
  useEffect(() => {
    if (windowWidth >= 768) {
      setSidebarOpen(false)
    }
  }, [windowWidth])

  // Show sidebar on desktop (≥768px) or when explicitly opened on mobile
  const showSidebar = isLoaded && isSignedIn && (windowWidth >= 768 || sidebarOpen)

  return (
    <BrowserRouter>
      <ToastProvider>
        {showSidebar && <Sidebar onClose={() => setSidebarOpen(false)} />}
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            marginLeft: isLoaded && isSignedIn && windowWidth >= 768 ? 260 : 0,
            transition: 'margin-left 0.3s',
          }}
        >
          <Navbar onMenuClick={() => setSidebarOpen(true)} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/login" element={<HomeRoute />} />

              <Route
                path="/dashboard"
                element={
                  <RequireAuth>
                    <DashboardPage />
                  </RequireAuth>
                }
              />

              <Route
                path="/projects/:id"
                element={
                  <RequireAuth>
                    <ProjectPage />
                  </RequireAuth>
                }
              />

              <Route
                path="/projects/:id/bids/new"
                element={
                  <RequireAuth>
                    <BidRequestPage />
                  </RequireAuth>
                }
              />

              <Route
                path="/projects/:id/sheets/:sheetId"
                element={
                  <RequireAuth>
                    <CanvasPage />
                  </RequireAuth>
                }
              />

              <Route
                path="/contractors"
                element={
                  <RequireAuth>
                    <GeneralContractorsListPage />
                  </RequireAuth>
                }
              />

              <Route
                path="/integrations"
                element={
                  <RequireAuth>
                    <IntegrationsPage />
                  </RequireAuth>
                }
              />

              <Route
                path="/projects/:id/estimate"
                element={
                  <RequireAuth>
                    <EstimatePage />
                  </RequireAuth>
                }
              />

              <Route
                path="/settings/pricing"
                element={
                  <RequireAuth>
                    <PricingPage />
                  </RequireAuth>
                }
              />

              <Route
                path="/settings"
                element={
                  <RequireAuth>
                    <Navigate to="/settings/pricing" replace />
                  </RequireAuth>
                }
              />

              <Route
                path="/users"
                element={
                  <RequireAdmin>
                    <UsersPage />
                  </RequireAdmin>
                }
              />

              <Route
                path="/approvals"
                element={
                  <RequireAdmin>
                    <ApprovalsPage />
                  </RequireAdmin>
                }
              />

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </div>
        </div>
      </ToastProvider>
    </BrowserRouter>
  )
}
